import React, { useState, useEffect, useCallback } from 'react';
import { Menu, X, ChevronDown } from 'lucide-react';

const MAGENTA = '#FF1744';

const CONTACT_EMAIL = 'hello@decentralizedcreative.com';
const CONTACT_CHANNELS = ['Email', 'Telegram', 'Signal', 'X DM'];
const CONTACT_PROMPTS = {
  Email: 'Email Address',
  Telegram: 'Enter your Telegram handle',
  Signal: 'Enter your Signal handle',
  'X DM': 'Enter your X handle'
};
const CONTACT_LABELS = {
  Email: 'Email',
  Telegram: 'Telegram handle',
  Signal: 'Signal handle',
  'X DM': 'X handle'
};
const EMPTY_FORM = {
  name: '',
  contactVia: '',
  contact: '',
  message: ''
};

const fieldStyle = {
  width: '100%',
  padding: '0.9rem 1.25rem',
  borderRadius: '50px',
  border: '1px solid #bbb',
  backgroundColor: '#fff',
  fontSize: '15px',
  fontFamily: 'inherit',
  color: '#000',
  outline: 'none',
  boxSizing: 'border-box'
};

function ContactModal({ onClose }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [error, setError] = useState('');

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  const update = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.contactVia || !form.message.trim()) {
      setError('Please add your name, a message, and how you would like me to reply.');
      return;
    }
    if (!form.contact.trim()) {
      setError(`Please add your ${CONTACT_LABELS[form.contactVia].toLowerCase()}.`);
      return;
    }
    if (form.contactVia === 'Email' && !/^\S+@\S+\.\S+$/.test(form.contact.trim())) {
      setError('Please enter a valid email address.');
      return;
    }
    setError('');

    // No form backend is connected yet, so this opens the visitor's email app
    // with the message pre-filled and addressed to hello@decentralizedcreative.com.
    const subject = `Website inquiry from ${form.name}`;
    const lines = [`Name: ${form.name}`, `Preferred contact: ${form.contactVia}`];
    lines.push(`${CONTACT_LABELS[form.contactVia]}: ${form.contact}`);
    lines.push('', form.message);
    const body = lines.join('\n');

    window.location.href =
      `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    onClose();
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
        zIndex: 2000
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="contact-title"
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: '#f5f5f5',
          borderRadius: '24px',
          maxWidth: '560px',
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto',
          padding: '2rem',
          position: 'relative',
          boxSizing: 'border-box'
        }}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close contact form"
          style={{
            position: 'absolute',
            top: '1rem',
            right: '1rem',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '0.25rem',
            color: '#000'
          }}
        >
          <X size={22} />
        </button>

        <h2 id="contact-title" style={{ fontSize: '26px', fontWeight: 600, color: '#000', margin: '0 0 0.5rem' }}>
          Tell me what you need help with
        </h2>
        <p style={{ fontSize: '15px', color: '#666', margin: '0 0 1.75rem', lineHeight: 1.5 }}>
          I'll get right back to you as soon as I can.
        </p>

        <form onSubmit={handleSubmit}>
          <input
            style={fieldStyle}
            type="text"
            placeholder="What should I call you? (nicknames are fine)"
            value={form.name}
            onChange={update('name')}
            autoComplete="nickname"
            required
          />

          <div style={{ position: 'relative', marginTop: '0.9rem' }}>
            <select
              style={{
                ...fieldStyle,
                appearance: 'none',
                WebkitAppearance: 'none',
                paddingRight: '3rem',
                cursor: 'pointer',
                color: form.contactVia ? '#000' : '#888'
              }}
              value={form.contactVia}
              onChange={update('contactVia')}
              required
            >
              <option value="" disabled>How should I get back to you?</option>
              {CONTACT_CHANNELS.map((channel) => (
                <option key={channel} value={channel}>{channel}</option>
              ))}
            </select>
            <ChevronDown
              size={18}
              aria-hidden="true"
              style={{
                position: 'absolute',
                right: '2.25rem',
                top: '50%',
                transform: 'translateY(-50%)',
                pointerEvents: 'none',
                color: '#000'
              }}
            />
          </div>

          {form.contactVia && (
            <input
              style={{ ...fieldStyle, marginTop: '0.9rem' }}
              type={form.contactVia === 'Email' ? 'email' : 'text'}
              placeholder={CONTACT_PROMPTS[form.contactVia]}
              value={form.contact}
              onChange={update('contact')}
              autoComplete={form.contactVia === 'Email' ? 'email' : 'off'}
              required
            />
          )}

          <textarea
            placeholder="Message"
            value={form.message}
            onChange={update('message')}
            rows={6}
            style={{
              ...fieldStyle,
              borderRadius: '24px',
              marginTop: '1.25rem',
              padding: '1rem 1.25rem',
              resize: 'vertical'
            }}
          />

          {error && (
            <p role="alert" style={{ color: '#FF1744', fontSize: '14px', margin: '1rem 0 0' }}>
              {error}
            </p>
          )}

          <button
            type="submit"
            style={{
              width: '100%',
              marginTop: '1.5rem',
              padding: '1rem',
              borderRadius: '50px',
              border: 'none',
              backgroundColor: MAGENTA,
              color: '#fff',
              fontSize: '15px',
              fontWeight: 500,
              fontFamily: 'inherit',
              cursor: 'pointer'
            }}
          >
            Submit
          </button>
        </form>
      </div>
    </div>
  );
}

export default function DecentralizedCreative() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);

  const openContact = (e) => {
    e.preventDefault();
    setContactOpen(true);
  };
  const closeContact = useCallback(() => setContactOpen(false), []);

  return (
    <div style={{ width: '100%', margin: 0, padding: 0 }}>
      {/* Navigation */}
      <nav style={{
        backgroundColor: '#000000',
        padding: '1rem 2rem',
        position: 'sticky',
        top: 0,
        zIndex: 1000,
        borderBottom: '1px solid #333'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <img src={process.env.PUBLIC_URL + '/avatar.png'} alt="Avatar" style={{ width: '50px', height: '50px', borderRadius: '50%', objectFit: 'cover' }} />
            <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#fff' }}>Decentralized Creative</h2>
          </div>

          {/* Desktop Menu */}
          <div style={{ display: 'flex', gap: '2rem', alignItems: 'center' }}>
            <a href="#home" style={{ textDecoration: 'none', color: '#fff', cursor: 'pointer', fontSize: '14px' }}>Home</a>
            <a href="#services" style={{ textDecoration: 'none', color: '#fff', cursor: 'pointer', fontSize: '14px' }}>Services</a>
            <a href="#contact" onClick={openContact} style={{ textDecoration: 'none', color: '#fff', cursor: 'pointer', fontSize: '14px' }}>Contact</a>
            <a href="#blog" style={{ textDecoration: 'none', color: '#fff', cursor: 'pointer', fontSize: '14px' }}>Blog</a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section style={{
        background: 'linear-gradient(135deg, #FFC107 0%, #FFC107 100%)',
        padding: '4rem 2rem',
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          maxWidth: '1000px',
          margin: '0 auto',
          position: 'relative',
          zIndex: 2
        }}>
          <div style={{
            display: 'inline-block',
            backgroundColor: '#FF1744',
            color: '#fff',
            padding: '0.5rem 1.5rem',
            borderRadius: '50px',
            fontSize: '12px',
            fontWeight: 600,
            marginBottom: '2rem',
            textTransform: 'uppercase',
            letterSpacing: '1px'
          }}>
            Innovative Creative Solutions
          </div>

          <h1 style={{
            fontSize: '48px',
            fontWeight: 700,
            color: '#fff',
            marginBottom: '2rem',
            lineHeight: '1.2'
          }}>
            Helping creative entrepreneurs like you embrace decentralized tech.
          </h1>

          <p style={{
            fontSize: '16px',
            color: '#fff',
            maxWidth: '700px',
            margin: '0 auto 2rem',
            lineHeight: '1.6'
          }}>
            At Decentralized Creative, we help you harness blockchain technology to elevate your digital presence and faster growth in the creative industry.
          </p>

          <div style={{
            display: 'flex',
            gap: '1rem',
            justifyContent: 'center',
            alignItems: 'center',
            flexWrap: 'wrap',
            marginBottom: '1rem'
          }}>
            <span style={{ fontSize: '14px', color: '#fff', fontWeight: 500 }}>⭐⭐⭐⭐⭐</span>
            <span style={{ fontSize: '14px', color: '#fff' }}>54 real people say about us</span>
          </div>
        </div>
      </section>

      {/* Crypto Section */}
      <section style={{
        backgroundColor: '#000000',
        padding: '4rem 2rem',
        color: '#fff'
      }}>
        <div style={{
          maxWidth: '1000px',
          margin: '0 auto'
        }}>
          <h2 style={{
            fontSize: '36px',
            fontWeight: 700,
            marginBottom: '1.5rem',
            lineHeight: '1.3'
          }}>
            Getting started in crypto can be daunting! Let me help you understand how to start accepting, saving and spending cryptocurrency.
          </h2>

          <button style={{
            backgroundColor: '#FF1744',
            color: '#fff',
            border: 'none',
            padding: '0.75rem 2rem',
            borderRadius: '50px',
            fontSize: '14px',
            fontWeight: 600,
            cursor: 'pointer',
            marginTop: '1rem'
          }}>
            Schedule a consultation with me and get started in crypto today
          </button>
        </div>
      </section>

      {/* Services Section */}
      <section style={{
        padding: '4rem 2rem',
        maxWidth: '1200px',
        margin: '0 auto'
      }}>
        <h2 style={{
          fontSize: '32px',
          fontWeight: 700,
          marginBottom: '3rem',
          textAlign: 'center',
          color: '#000'
        }}>
          Need some creative work done? I got you
        </h2>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '2rem'
        }}>
          {[
            { title: 'Explainer Videos', emoji: '🎬' },
            { title: 'Promo Videos', emoji: '📺' },
            { title: 'Podcast Editing', emoji: '🎙️' },
            { title: 'Crypto Consultation', emoji: '🔐' }
          ].map((service, i) => (
            <div key={i} style={{
              backgroundColor: '#f5f5f5',
              padding: '2rem',
              borderRadius: '12px',
              border: '1px solid #e0e0e0',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '48px', marginBottom: '1rem' }}>
                {service.emoji}
              </div>
              <h3 style={{
                fontSize: '18px',
                fontWeight: 600,
                color: '#000',
                marginBottom: '0.5rem'
              }}>
                {service.title}
              </h3>
              <p style={{ fontSize: '14px', color: '#666', lineHeight: '1.6' }}>
                [Service description coming soon]
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Portfolio Section */}
      <section style={{ padding: '4rem 2rem', backgroundColor: '#f9f9f9' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 style={{
            fontSize: '28px',
            fontWeight: 700,
            marginBottom: '3rem',
            textAlign: 'center',
            color: '#000'
          }}>
            Recent Work
          </h2>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '2rem'
          }}>
            {[1, 2, 3].map(i => (
              <div key={i} style={{
                backgroundColor: '#fff',
                borderRadius: '12px',
                overflow: 'hidden',
                border: '1px solid #e0e0e0'
              }}>
                <div style={{
                  height: '200px',
                  backgroundColor: '#ddd',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#999'
                }}>
                  [Portfolio item {i}]
                </div>
                <div style={{ padding: '1.5rem' }}>
                  <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#000', marginBottom: '0.5rem' }}>
                    Project Title
                  </h3>
                  <p style={{ fontSize: '14px', color: '#666', lineHeight: '1.6' }}>
                    [Project description coming soon]
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer style={{
        backgroundColor: '#000000',
        color: '#fff',
        padding: '3rem 2rem',
        borderTop: '1px solid #333'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '2rem',
            marginBottom: '2rem'
          }}>
            <div>
              <h4 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '1rem' }}>Navigation</h4>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                <li style={{ marginBottom: '0.5rem' }}><a href="#" style={{ color: '#999', textDecoration: 'none', fontSize: '14px' }}>Home</a></li>
                <li style={{ marginBottom: '0.5rem' }}><a href="#" style={{ color: '#999', textDecoration: 'none', fontSize: '14px' }}>Services</a></li>
                <li style={{ marginBottom: '0.5rem' }}><a href="#contact" onClick={openContact} style={{ color: '#999', textDecoration: 'none', fontSize: '14px' }}>Contact</a></li>
                <li style={{ marginBottom: '0.5rem' }}><a href="#" style={{ color: '#999', textDecoration: 'none', fontSize: '14px' }}>Blog</a></li>
              </ul>
            </div>
            <div>
              <h4 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '1rem' }}>Connect</h4>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                <li style={{ marginBottom: '0.5rem' }}><a href="#" style={{ color: '#999', textDecoration: 'none', fontSize: '14px' }}>Twitter</a></li>
                <li style={{ marginBottom: '0.5rem' }}><a href="#" style={{ color: '#999', textDecoration: 'none', fontSize: '14px' }}>LinkedIn</a></li>
                <li style={{ marginBottom: '0.5rem' }}><a href="#" style={{ color: '#999', textDecoration: 'none', fontSize: '14px' }}>Discord</a></li>
              </ul>
            </div>
          </div>

          <div style={{
            borderTop: '1px solid #333',
            paddingTop: '1.5rem',
            textAlign: 'center',
            color: '#666',
            fontSize: '14px'
          }}>
            <p>© 2026 Decentralized Creative. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {contactOpen && <ContactModal onClose={closeContact} />}
    </div>
  );
}
